import { MutationObserver, useMutationState, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { keys } from '../../api/keys'
import { emitSuggestionAccepted, emitSuggestionDismissed } from './recurringAnalytics'
import { acceptSuggestion, dismissSuggestion } from './recurringApi'

/** Nothing was sent: this suggestion already has a write out. */
export class SuggestionBusyError extends Error {
  constructor() {
    super('suggestion-busy')
    this.name = 'SuggestionBusyError'
  }
}

const pending = (client: QueryClient, mutationKey: readonly unknown[]) =>
  client.getMutationCache().findAll({ mutationKey, exact: true, status: 'pending' }).length > 0

/**
 * Run one write through the mutation cache, so its lock and its settle outlive
 * the component that started it. A `MutationObserver` rather than `useMutation`
 * because Add all starts N of these, one per suggestion, each under its own
 * key — a hook is one key. The guard is `useLifecycle`'s: refuse, never queue.
 *
 * `onSettled` is mutation-level and does the invalidation, so a screen that
 * unmounted mid-request still refreshes the list and the badge. It is not
 * awaited: a slow refetch must not hold the lock.
 */
function run<T>(
  client: QueryClient,
  mutationKey: readonly unknown[],
  mutationFn: () => Promise<T>,
  linked: boolean,
  onSuccess: () => void,
): Promise<T> {
  if (pending(client, mutationKey)) return Promise.reject(new SuggestionBusyError())
  const observer = new MutationObserver<T, Error, void>(client, {
    mutationKey,
    mutationFn,
    // Mutation-level, so an accept that lands after the screen unmounted still counts.
    onSuccess,
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
      // Accepting backfills past payments, which are links on transactions.
      if (linked) void client.invalidateQueries({ queryKey: keys.transactions.all() })
    },
  })
  // `reset` removes the observer from the mutation, which re-arms its gc
  // timeout. Without it the mutation (and its lock entry) stays in the cache
  // for the whole session: an observer that is never removed is never collected.
  return observer.mutate().finally(() => observer.reset())
}

/** The ids with an accept or dismiss in flight, read from the mutation cache. */
export function useBusySuggestions(): ReadonlySet<string> {
  const ids = useMutationState({
    filters: {
      status: 'pending',
      predicate: (m) => {
        const kind = m.options.mutationKey?.[1]
        return kind === 'suggestion-accept' || kind === 'suggestion-dismiss'
      },
    },
    select: (m) => String(m.options.mutationKey?.[2]),
  })
  return new Set(ids)
}

export function useSuggestionWrites(): {
  accept: (id: string) => Promise<number>
  dismiss: (id: string) => Promise<void>
} {
  const client = useQueryClient()
  return {
    accept: useCallback((id) => run(client, keys.recurring.suggestionAccept(id), () => acceptSuggestion(id), true, emitSuggestionAccepted), [client]),
    dismiss: useCallback((id) => run(client, keys.recurring.suggestionDismiss(id), () => dismissSuggestion(id), false, emitSuggestionDismissed), [client]),
  }
}
